import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import cacheBuster from "./utils/cacheBuster";

// Auth Pages
import Login from "./Pages/Auth/Login";
import RestorePassword from "./Pages/Auth/RestorePassword";

// Dashboard Pages
import Home from "./Pages/Dashboard/Home";
import AuthLayout from "./Layout/Auth/AuthLayout";
import DashboardLayout from "./Layout/Dashboard/DashboardLayout";
import Support from "./Pages/Dashboard/Support";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Contact from "./Pages/Dashboard/Account/Contact";
import Billing from "./Pages/Dashboard/Account/Billing";
import UpdatePassword from "./Pages/Dashboard/Account/UpdatePassword";
import AudioAd from "./Pages/Dashboard/Admax/AudioAd";
import BannerAd from "./Pages/Dashboard/Admax/BannerAd";
import VideoAd from "./Pages/Dashboard/Admax/VideoAd";
import Basic from "./Pages/Dashboard/Configure/Basic";
import Audio from "./Pages/Dashboard/Configure/Audio";
import Social from "./Pages/Dashboard/Configure/Social";
import Team from "./Pages/Dashboard/Team/Team";
import User from "./Pages/Dashboard/Users/User";
import Moderator from "./Pages/Dashboard/Moderator/Moderator";
import Schedule from "./Pages/Dashboard/Schedule/Schedule";
import AnalyticsView from "./Pages/Dashboard/AnalyticsView/AnalyticsView";
import Podcast from "./Pages/Dashboard/Configure/Podcast";
import RegistrationSteps from "./Pages/Setup/RegistrationSteps";
import SetupLayout from "./Layout/Setup/SetupLayout";
import RegistrationStepsOne from "./Pages/Setup/RegistrationStepsOne";
import List from "./Pages/Dashboard/Invitations/List";
import Sessions from "./Pages/Dashboard/Sessions/Sessions";
import AppsList from "./components/Apps/AppsList";
import ApiLimiting from "./Pages/Dashboard/Account/ApiLimiting";
import { useSelector } from "react-redux";
import ProtectedRoute from "./components/ProtectedRoute";
import SearchUser from "./Pages/Dashboard/Users/SearchUser";
import StationGroups from "./Pages/Dashboard/StationGroups/StationGroups";
import StationGroupsLayout from "./Layout/StationGroups/StationGroupsLayout";
import GroupAdminLayout from "./Layout/GroupAdmin/GroupAdminLayout";

const App = () => {
  const { isRoot } = useSelector((state) => state.auth);

  // Initialize cache busting
  useEffect(() => {
    cacheBuster.init();
  }, []);

  return (
    <Router>

      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeButton={true} />
      <Routes>
        <Route path="/auth" element={<AuthLayout />}>
          <Route path="login" element={<Login />} />
          <Route path="restore-password" element={<RestorePassword />} />
        </Route>

        <Route path="/setup" element={<SetupLayout />}>
          <Route path="" element={<RegistrationSteps />} />
          <Route path="1" element={<RegistrationStepsOne />} />
        </Route>

        <Route path="/admin" element={<DashboardLayout />}>
          <Route path="dashboard" element={<Home />} />
          {/* account routes - accessible by all authenticated users */}
          <Route path="dashboard/account/contact" element={
            <ProtectedRoute>
              <Contact />
            </ProtectedRoute>
          } />
          <Route path="dashboard/account/billing" element={
            <ProtectedRoute>
              <Billing />
            </ProtectedRoute>
          } />
          <Route path="dashboard/account/update-password" element={
            <ProtectedRoute>
              <UpdatePassword />
            </ProtectedRoute>
          } />
          {/* admax routes - accessible by admin and root */}
          <Route path="dashboard/admax/ads" element={
            <ProtectedRoute allowedRoles={['admin', 'root']}>
              <BannerAd />
            </ProtectedRoute>
          } />
          <Route path="dashboard/admax/audio-ads" element={
            <ProtectedRoute allowedRoles={['admin', 'root']}>
              <AudioAd />
            </ProtectedRoute>
          } />
          <Route path="dashboard/admax/video-ads" element={
            <ProtectedRoute allowedRoles={['admin', 'root']}>
              <VideoAd />
            </ProtectedRoute>
          } />
          {/* configure routes - accessible by admin and root */}
          <Route path="dashboard/configure/basic" element={
            <ProtectedRoute allowedRoles={['admin', 'root']}>
              <Basic />
            </ProtectedRoute>
          } />
          <Route path="dashboard/configure/audio" element={
            <ProtectedRoute allowedRoles={['admin', 'root']}>
              <Audio />
            </ProtectedRoute>
          } />
          <Route path="dashboard/configure/podcast" element={
            <ProtectedRoute allowedRoles={['admin', 'root']}>
              <Podcast />
            </ProtectedRoute>
          } />
          <Route path="dashboard/configure/social" element={
            <ProtectedRoute allowedRoles={['admin', 'root']}>
              <Social />
            </ProtectedRoute>
          } />
          {/* support routes - accessible by all authenticated users */}
          <Route path="dashboard/support" element={
            <ProtectedRoute>
              <Support />
            </ProtectedRoute>
          } />
          
          {/* analytics - accessible by admin and root */}
          <Route path="dashboard/analytics" element={
            <ProtectedRoute allowedRoles={['admin', 'root']}>
              <AnalyticsView />
            </ProtectedRoute>
          } />
          
          {/* team management - accessible by admin and root */}
          <Route path="dashboard/team" element={
            <ProtectedRoute allowedRoles={['admin', 'root']}>
              <Team />
            </ProtectedRoute>
          } />
          
          {/* user management - accessible by admin and root */}
          <Route path="dashboard/users" element={
            <ProtectedRoute allowedRoles={['admin', 'root']}>
              <User />
            </ProtectedRoute>
          } />
          
          {/* moderator - accessible by all authenticated users */}
          <Route path="dashboard/moderator" element={
            <ProtectedRoute>
              <Moderator />
            </ProtectedRoute>
          } />
          
          {/* schedule - accessible by admin and root */}
          <Route path="dashboard/schedule" element={
            <ProtectedRoute allowedRoles={['admin', 'root']}>
              <Schedule />
            </ProtectedRoute>
          } />


          {/* root admin only routes */}
          <Route path="dashboard/invitations" element={
            <ProtectedRoute allowedRoles={['root','reseller-113fm']} fallbackPath="/admin/dashboard/moderator">
              <List />
            </ProtectedRoute>
          } />
          <Route path="dashboard/account/api-limiting" element={
            <ProtectedRoute allowedRoles={['root']} fallbackPath="/admin/dashboard/moderator">
              <ApiLimiting />
            </ProtectedRoute>
          } />
          <Route path="dashboard/sessions" element={
            <ProtectedRoute allowedRoles={['root']} fallbackPath="/admin/dashboard/moderator">
              <Sessions />
            </ProtectedRoute>
          } />
          <Route path="dashboard/apps" element={
            <ProtectedRoute allowedRoles={['root']} fallbackPath="/admin/dashboard/moderator">
              <AppsList />
            </ProtectedRoute>
          } />
          <Route path="dashboard/user-search" element={
            <ProtectedRoute allowedRoles={['root']} fallbackPath="/admin/dashboard/moderator">
              <SearchUser />
            </ProtectedRoute>
          } />
          </Route>

        {/* Station Groups: minimal layout (no sidebar/header) for group_admin - only at this path */}
        <Route path="/admin/station-groups" element={
          <ProtectedRoute allowedRoles={['admin', 'group_admin']} fallbackPath="/admin/dashboard/moderator">
            <StationGroupsLayout />
          </ProtectedRoute>
        } />

        <Route path="/admin/group-admin" element={
          <ProtectedRoute allowedRoles={['group_admin']} fallbackPath="/admin/station-groups">
            <GroupAdminLayout />
          </ProtectedRoute>
        }>
          <Route path="account/contact" element={
            <ProtectedRoute allowedRoles={['group_admin']} fallbackPath="/admin/station-groups">
              <Contact targetGroupAdmin={true} accountBasePath="/admin/group-admin/account" showUpdatePassword={true} />
            </ProtectedRoute>
          } />
          <Route path="account/update-password" element={
            <ProtectedRoute allowedRoles={['group_admin']} fallbackPath="/admin/station-groups">
              <UpdatePassword targetGroupAdmin={true} accountBasePath="/admin/group-admin/account" showUpdatePassword={true} />
            </ProtectedRoute>
          } />
          <Route path="support" element={
            <ProtectedRoute allowedRoles={['group_admin']} fallbackPath="/admin/station-groups">
              <Support hideContactSubheading={true} />
            </ProtectedRoute>
          } />
        </Route>

        <Route path="/*" element={<Navigate to="/auth/login" />} />
      </Routes>
    </Router>
  );
};

export default App;
